/**
 * Sentry initialization for the frontend.
 *
 * This module has side effects and MUST be imported before anything else in
 * main.tsx so the SDK installs its global handlers before app code runs.
 *
 * Scope of what we send:
 *  - Error monitoring + performance tracing only. Session Replay is deliberately
 *    NOT enabled — PostHog already provides session replay, so we don't double
 *    up (or spend Sentry replay quota).
 *  - We skip init on localhost so local `vite` dev doesn't spam the dev project;
 *    the deployed dev site (dev.bbxt.org) and prod still report.
 *
 * DSN is non-secret and comes from VITE_SENTRY_DSN (see .env.development /
 * .env.production). Release/build id are injected by vite.config.ts.
 */
import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;

const host = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '';

// Match the backend's environment tag ('dev' / 'prod') rather than Vite's
// 'development' / 'production' so events line up across projects.
const environment = import.meta.env.MODE === 'production' ? 'prod' : 'dev';

if (dsn && !isLocalhost) {
  Sentry.init({
    dsn,
    environment,
    release: __SENTRY_RELEASE__,

    integrations: [
      // Page load + SPA navigation transactions (route names come from the
      // wrapped data router in router.tsx).
      Sentry.browserTracingIntegration(),
    ],

    // Full sampling on dev for signal while stabilizing; sample down in prod.
    tracesSampleRate: environment === 'prod' ? 0.2 : 1.0,

    // Only attach distributed-tracing headers to same-origin requests. The
    // AppSync/Cognito endpoints are cross-origin and wouldn't forward the
    // headers to Lambda anyway, and adding them would trigger CORS preflights.
    tracePropagationTargets: [/^\//],
  });
}
